/**
 * Masks sensitive contact info in message text.
 * - Phones: keep first 2 digits, mask the rest with * (06********)
 * - Emails: j****@g****.com
 * - Social handles / URLs: replaced by [masqué]
 *
 * Used in messaging UI when the two participants don't yet have a paid+accepted booking.
 */

const EMAIL_RE = /([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+)\.([a-zA-Z]{2,})/g;

// Phone: 9-15 digits, possibly with separators (spaces, dots, dashes, parentheses, +)
const PHONE_RE = /(\+?\d[\d\s.\-()]{8,18}\d)/g;

const SOCIAL_RE =
  /\b(whatsapp|wa\.me\/\S*|t\.me\/\S*|telegram|snapchat|snap\.com\/\S*|instagram|insta|facebook|fb\.com\/\S*|messenger|tiktok|discord(?:\.gg\/\S*)?|signal\.me\/\S*)\b/gi;

const URL_RE = /\b((?:https?:\/\/|www\.)[^\s]+)/gi;

const maskPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 9) return raw;
  const visible = digits.slice(0, 2);
  return visible + "*".repeat(digits.length - 2);
};

const maskEmailLocal = (local: string): string => {
  if (local.length <= 1) return local + "***";
  return local[0] + "*".repeat(Math.max(3, local.length - 1));
};

const maskEmailDomain = (domain: string): string => {
  if (domain.length <= 1) return "*".repeat(3);
  return domain[0] + "*".repeat(Math.max(3, domain.length - 1));
};

/**
 * Strips internal system metadata tags from a message before display.
 * Tags look like `[booking:UUID:kind]`, `[payment:...:...]`, `[system:...]`, etc.
 * They are used internally for idempotency / routing and must never reach the UI.
 */
const SYSTEM_TAG_RE = /\[(?:booking|payment|reservation|user|profile|system|stripe|dispute|notification)[^\]\n]*\]/gi;

export const stripSystemTags = (text: string | null | undefined): string => {
  if (!text) return "";
  return text
    .replace(SYSTEM_TAG_RE, "")
    // collapse leftover blank lines / trailing whitespace
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const maskMessageContent = (text: string): string => {
  const stripped = stripSystemTags(text);
  if (!stripped) return stripped;
  let result = stripped;


  // Emails first (before phone — emails contain digits sometimes)
  result = result.replace(EMAIL_RE, (_m, local: string, domain: string, tld: string) => {
    return `${maskEmailLocal(local)}@${maskEmailDomain(domain)}.${tld}`;
  });

  // Phones
  result = result.replace(PHONE_RE, (m: string) => maskPhone(m));

  // Social mentions
  result = result.replace(SOCIAL_RE, "[réseau masqué]");

  // External URLs (skip clickandgard)
  result = result.replace(URL_RE, (m: string) =>
    /clickandgard/i.test(m) ? m : "[lien masqué]"
  );

  return result;
};

export const containsMaskableContent = (text: string): boolean => {
  if (!text) return false;
  const cleaned = text.replace(/\s/g, "");
  return (
    EMAIL_RE.test(text) ||
    /\d{9,}/.test(cleaned) ||
    SOCIAL_RE.test(text) ||
    URL_RE.test(text)
  );
};
